"use client";
import { motion } from "framer-motion";
import LocationMap from "@/components/Map/LocationMap";
import FAQSection from "@/components/sections/FAQSection";
import ContactFormClient from "@/components/sections/contact-form-client";
import ContactInfoClient from "@/components/sections/contact-info-client";
import { ContactPageData } from "@/types/index";

interface ContactPageAnimatedProps {
  contactPageData: ContactPageData;
  lang: string;
  submitContactForm: (formData: FormData) => Promise<{ success: boolean; error?: string }>;
}

export default function ContactPageAnimated({ contactPageData, lang, submitContactForm }: ContactPageAnimatedProps) {
  // Animation variants
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "circOut" as const,
      },
    },
  };

  return (
    <>
      {/* Main Content Section */}
      <motion.div
        className="relative z-10 mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={sectionVariants}
      >
        <div className="relative z-10 py-12 lg:py-25">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-8 lg:gap-16 mx-auto">
            {/* Left Side - Contact Information */}
            <ContactInfoClient contactPageData={contactPageData} lang={lang} />

            {/* Right Side - Contact Form */}
            <ContactFormClient 
              contactPageData={contactPageData} 
              lang={lang} 
              submitContactForm={submitContactForm}
            />
          </div>
        </div>
      </motion.div>

      {/* Map Section */}
      <motion.div
        className="relative z-10 mx-auto"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={sectionVariants}
      >
        <div className="mx-auto">
          {/* Map Container */}
          <div className="w-full h-[500px] lg:h-[600px]">
            <LocationMap
              className="w-full h-full"
              companyName={contactPageData?.mapPopupTextEn}
              companyNameBn={contactPageData?.mapPopupTextBn}
            />
          </div>
        </div>
      </motion.div>

      {/* FAQ Section */}
      <motion.div
        className="py-12 md:py-[100px]"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={sectionVariants}
      >
        <FAQSection
          title={contactPageData?.faq?.title || "FAQ"}
          faqData={contactPageData?.faq?.accordions || []}
        />
      </motion.div>
    </>
  );
}
